import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Layers, Loader2, Sparkles, ArrowUpRight, CheckCircle2, CalendarClock } from "lucide-react";

const TONES = [
  { id: "streetwear", label: "Streetwear" },
  { id: "premium", label: "Premium" },
  { id: "fun", label: "Fun" },
  { id: "minimal", label: "Minimal" },
];
const LENGTHS = [
  { id: "court", label: "Court" },
  { id: "moyen", label: "Moyen" },
  { id: "long", label: "Long" },
];

export default function BatchGenerator() {
  const { activeId, activeClient } = useClient();
  const [brief, setBrief] = useState("");
  const [count, setCount] = useState(10);
  const [tone, setTone] = useState("streetwear");
  const [length, setLength] = useState("moyen");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // empty = toute la galerie
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleStart, setScheduleStart] = useState("");
  const [intervalDays, setIntervalDays] = useState(1);
  const [publishTime, setPublishTime] = useState("18:00");

  useEffect(() => {
    if (!activeId) return;
    setSelectedIds([]);
    api.get("/products", { params: { client_id: activeId } })
      .then(({ data }) => setProducts(data))
      .catch(() => {});
  }, [activeId]);

  const toggleProduct = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const generate = async () => {
    if (!activeId) return toast.error("Sélectionne une marque");
    if (scheduleOn && !scheduleStart) return toast.error("Choisis une date de début de planification");
    setLoading(true);
    setResults([]);
    try {
      const payload = {
        client_id: activeId,
        count: Number(count),
        tone,
        length,
        brief: brief || null,
        product_ids: selectedIds.length ? selectedIds : null,
      };
      if (scheduleOn) {
        payload.schedule_start = new Date(
          `${scheduleStart}T${publishTime || "18:00"}`
        ).toISOString();
        payload.interval_days = Number(intervalDays) || 1;
        payload.publish_time = publishTime || "18:00";
      }
      const { data } = await api.post("/generate-batch", payload);
      setResults(data);
      toast.success(
        scheduleOn
          ? `${data.length} posts générés et planifiés`
          : `${data.length} posts générés en brouillon`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const handle = activeClient?.instagram_handle || "votre.marque";

  return (
    <>
      <PageHeader overline={`Génération en lot · @${handle}`} title="Lot IA">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground hidden md:inline">
          Plusieurs posts, une seule demande
        </span>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* config */}
        <div className="lg:col-span-4 p-6 lg:p-8 lg:border-r border-white/10 space-y-6">
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              La demande / DA
            </label>
            <textarea
              data-testid="batch-brief-input"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={5}
              placeholder="Décris ta demande : thème, direction artistique, message, promo… Ex : série de posts autour de la nouvelle collection, ambiance été, ton énergique."
              className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-y"
            />
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Nombre de posts
            </label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 15, 20].map((n) => (
                <button
                  key={n}
                  data-testid={`count-${n}`}
                  onClick={() => setCount(n)}
                  className={`w-12 py-2 rounded-sm text-sm font-bold border transition-all duration-150 ${
                    Number(count) === n
                      ? "bg-accent border-accent text-accent-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {n}
                </button>
              ))}
              <input
                data-testid="count-input"
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                className="w-16 bg-transparent border border-white/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary [color-scheme:dark]"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Max 20 · chaque post a un angle différent</p>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Ton
            </label>
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`batch-tone-${t.id}`}
                  onClick={() => setTone(t.id)}
                  className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                    tone === t.id
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Longueur
            </label>
            <div className="flex flex-wrap gap-2">
              {LENGTHS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`batch-length-${l.id}`}
                  onClick={() => setLength(l.id)}
                  className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                    length === l.id
                      ? "bg-accent border-accent text-accent-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Produits utilisés
            </label>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-muted-foreground">
                {selectedIds.length ? `${selectedIds.length} sélectionné(s)` : "Toute la galerie"}
              </span>
              {selectedIds.length > 0 && (
                <button
                  data-testid="batch-clear-products"
                  onClick={() => setSelectedIds([])}
                  className="text-[11px] text-accent uppercase tracking-wider"
                >
                  Tout désélectionner
                </button>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="batch-product-picker">
              {products.map((p) => (
                <button
                  key={p.id}
                  data-testid={`batch-product-${p.id}`}
                  onClick={() => toggleProduct(p.id)}
                  className={`shrink-0 w-16 h-16 border rounded-sm bg-[#0d0d0d] overflow-hidden transition-all duration-150 ${
                    selectedIds.includes(p.id)
                      ? "border-accent ring-1 ring-accent"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          </div>

          <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                data-testid="batch-schedule-toggle"
                checked={scheduleOn}
                onChange={(e) => setScheduleOn(e.target.checked)}
                className="accent-[#CC4B37]"
              />
              <span className="text-xs tracking-[0.15em] uppercase font-bold flex items-center gap-1.5">
                <CalendarClock className="h-4 w-4" /> Planifier automatiquement
              </span>
            </label>
            {scheduleOn && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Début</span>
                  <input
                    data-testid="batch-start-date"
                    type="date"
                    value={scheduleStart}
                    onChange={(e) => setScheduleStart(e.target.value)}
                    className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary [color-scheme:dark]"
                  />
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Heure</span>
                  <input
                    data-testid="batch-time"
                    type="time"
                    value={publishTime}
                    onChange={(e) => setPublishTime(e.target.value)}
                    className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary [color-scheme:dark]"
                  />
                </div>
                <div className="col-span-2">
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Intervalle entre posts (jours)
                  </span>
                  <input
                    data-testid="batch-interval"
                    type="number"
                    min={1}
                    value={intervalDays}
                    onChange={(e) => setIntervalDays(Math.max(1, Number(e.target.value) || 1))}
                    className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary [color-scheme:dark]"
                  />
                </div>
                <p className="col-span-2 text-[10px] text-muted-foreground">
                  1 post par créneau, espacés de l'intervalle choisi, à l'heure indiquée.
                </p>
              </div>
            )}
          </div>

          <button
            data-testid="generate-batch-button"
            onClick={generate}
            disabled={loading || !activeId}
            className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm py-3.5 rounded-sm flex items-center justify-center gap-2 hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Génération en cours…" : `Générer ${count} posts`}
          </button>
        </div>

        {/* results */}
        <div className="lg:col-span-8 p-6 lg:p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                L'IA rédige {count} posts uniques à partir de ta demande…
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-sm p-12 text-center">
              <Layers className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                Décris ta demande à gauche, choisis le nombre de posts, et l'IA génère
                d'un coup une série de posts (visuel + légende + hashtags) enregistrés en brouillon.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                  {results.length} posts enregistrés en brouillon
                </div>
                <Link
                  to="/calendrier"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  Voir dans le calendrier <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="batch-results">
                {results.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`batch-post-${i}`}
                    className="border border-white/10 rounded-sm bg-secondary overflow-hidden animate-fade-up"
                    style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
                  >
                    <div className="flex">
                      <div className="w-28 shrink-0 bg-[#0d0d0d] flex items-center justify-center">
                        <img src={p.image_url} alt="" className="w-full h-full object-contain p-2" />
                      </div>
                      <div className="p-3 flex-1 min-w-0">
                        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">
                          Post {i + 1}
                        </p>
                        <p className="text-xs leading-snug line-clamp-4">{p.caption}</p>
                      </div>
                    </div>
                    {p.hashtags?.length > 0 && (
                      <p className="text-[10px] text-[#4a9dff] px-3 py-2 border-t border-white/10 break-words line-clamp-2">
                        {p.hashtags.map((h) => `#${h}`).join(" ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
